import { useCurrencyHelpers } from '@plato/app-shared/hooks/use-currency-helpers';
import { SrInsightType as InsightType } from '@plato/data/starrocks/types';
import { formatPercentage } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { useTranslation } from 'react-i18next';
import type { ChurnDimension, ChurnWarningV2TemplateArgs } from '../../../server/dto/insight-dto.ts';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { churnV2CardDimension } from './churn-v2-dimension.ts';
import { churnWarningPeriodArgs } from './churn-warning-periods.ts';

type InsightReasonTextProps = {
    insight: InsightListItem['insight'];
    categoryName: string;
};

function formatDate(dateString: string, locale: string): string {
    try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat(locale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }).format(date);
    } catch {
        return dateString;
    }
}

function buildDimensionArgs(
    dim: ChurnDimension,
    args: ChurnWarningV2TemplateArgs,
    toCurrencyString: (value: number) => string,
    locale: string,
    categoryName: string,
): Record<string, string | number> {
    const argsByDimension = {
        d1_silence: {
            d1_days_since_order: args.d1_days_since_order ?? 0,
            d1_normal_order_cadence: args.d1_normal_order_cadence ?? 0,
            category_name: categoryName,
        },
        d2_cadence: {
            d2_decline_pct: formatPercentage((args.d2_decline_pct ?? 0) / 100, { locale }),
            d2_period_length: args.d2_period_length ?? 0,
        },
        d3_annual: {
            d3_revenue_dec_eur: toCurrencyString(args.d3_revenue_dec_eur ?? 0),
            d3_decline_pct: formatPercentage((args.d3_decline_pct ?? 0) / 100, { locale }),
            ...churnWarningPeriodArgs(12, locale),
        },
        d4_seasonal: {
            d4_decline_pct: formatPercentage((args.d4_decline_pct ?? 0) / 100, { locale }),
            ...churnWarningPeriodArgs(args.d4_period_length ?? 3, locale),
        },
        d5_revenue: {
            d5_revenue_change_pct: formatPercentage((args.d5_revenue_change_pct ?? 0) / 100, { locale }),
        },
        d6_momentum: {
            d6_decline_pct: formatPercentage((args.d6_decline_pct ?? 0) / 100, { locale }),
            ...churnWarningPeriodArgs(6, locale),
        },
        d7_concentration: {
            d7_current_sku_count: args.d7_current_sku_count ?? 0,
            d7_prev_sku_count: args.d7_prev_sku_count ?? 0,
        },
        d8_global: {
            d8_total_decline_pct: formatPercentage((args.d8_total_decline_pct ?? 0) / 100, { locale }),
        },
    } satisfies Record<ChurnDimension, Record<string, string | number>>;

    return argsByDimension[dim];
}

export function InsightReasonText({ insight, categoryName }: InsightReasonTextProps) {
    const { t } = useTranslation('insights');
    const locale = useResolvedLocale();
    const { toCurrencyString } = useCurrencyHelpers();

    const getChurnDimensionReason = (
        dim: ChurnDimension,
        reasonArgs: Record<string, string | number>,
    ): string => {
        switch (dim) {
            case 'd1_silence':
                return t('churn_v2_dimension__d1_silence', reasonArgs);
            case 'd2_cadence':
                return t('churn_v2_dimension__d2_cadence', reasonArgs);
            case 'd3_annual':
                return t('churn_v2_dimension__d3_annual', reasonArgs);
            case 'd4_seasonal':
                return t('churn_v2_dimension__d4_seasonal', reasonArgs);
            case 'd5_revenue':
                return t('churn_v2_dimension__d5_revenue', reasonArgs);
            case 'd6_momentum':
                return t('churn_v2_dimension__d6_momentum', reasonArgs);
            case 'd7_concentration':
                return t('churn_v2_dimension__d7_concentration', reasonArgs);
            case 'd8_global':
                return t('churn_v2_dimension__d8_global', reasonArgs);
        }
    };

    const generateReasonText = (): string => {
        switch (insight.insight_type) {
            case InsightType.PREVIOUSLY_PURCHASED: {
                return t('reason_template__previously_purchased');
            }

            case InsightType.CROSS_SELL: {
                const args = insight.template_arguments;
                if (!args) {
                    return '';
                }
                return t('reason_template__cross_sell', {
                    actionable_entity_name: args.actionable_entity_name,
                    explainer_category_name_1: args.explainer_category_name_1,
                    explainer_category_name_2: args.explainer_category_name_2,
                    revenue_potential: toCurrencyString(args.revenue_potential),
                    revenue_potential_percentage: args.revenue_potential_percentage.toFixed(1),
                });
            }

            case InsightType.CHURN_WARNING: {
                const args = insight.template_arguments;
                if (!args) {
                    return '';
                }
                // Not top_dimension_1: the percentage in this sentence has to describe the same
                // window as the EUR figure on the card, which is computed over the revenue-impact
                // dimension (RETAIN-1162).
                const dim = churnV2CardDimension(args);
                return getChurnDimensionReason(
                    dim,
                    buildDimensionArgs(dim, args, toCurrencyString, locale, categoryName),
                );
            }

            case InsightType.GLOBAL_CHURN_WARNING: {
                return t('reason_template__global_churn_warning');
            }

            case InsightType.FIRST_PURCHASE: {
                const args = insight.template_arguments;
                if (!args) {
                    return '';
                }
                return t('reason_template__first_purchase', {
                    order_date: formatDate(args.order_date, locale),
                    actionable_entity_name: args.actionable_entity_name,
                });
            }

            case InsightType.SHARE_OF_WALLET: {
                const args = insight.template_arguments;
                if (!args) {
                    return '';
                }
                return t('reason_template__share_of_wallet', {
                    amount_in_category: toCurrencyString(args.amount_in_category),
                    revenue_potential_in_category: toCurrencyString(args.revenue_potential_in_category),
                });
            }

            case InsightType.INDUSTRY_TOP: {
                const args = insight.template_arguments;
                if (!args) {
                    return '';
                }
                return t('reason_template__industry_top', {
                    penetration_percentage_formatted: formatPercentage(
                        args.penetration_percentage_formatted / 100,
                        { locale },
                    ),
                    industry_sector: args.industry_sector,
                });
            }

            case InsightType.GLOBAL_TOP: {
                const args = insight.template_arguments;
                if (!args) {
                    return '';
                }
                return t('reason_template__global_top', {
                    penetration_percentage_formatted: formatPercentage(
                        args.penetration_percentage_formatted / 100,
                        { locale },
                    ),
                });
            }

            default: {
                return '';
            }
        }
    };

    return <span>{generateReasonText()}</span>;
}
